import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {api} from 'nutshell-core/api';
import type {HttpErrorObject} from 'nutshell-core/utils/errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {FormField} from 'shells/form/form-field';
import {FooterWithControls, RightFrame, ModalMain} from 'shells/modal/step-modal/layout';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';
import {ModalTitle} from 'shells/typography/modal-title';

import {getServiceNoun} from '../../../calls/helpers';

import type {SetupError} from './error-handling-utils';
import {
    ERROR_CODE_MAP,
    PHONE_NUMBER_ALREADY_VALIDATED,
    PHONE_NUMBER_NEW_INSTANCE_AND_REQUIRES_VERIFICATION,
    PHONE_NUMBER_THROTTLED,
    getErrorFlashBannerFromCode,
    getErrorMessageFromCode,
} from './error-handling-utils';

type Props = {
    history: RouterHistory;
};

export function ClickToCallSetup(props: Props) {
    const {history} = props;

    const [name, setName] = React.useState<string>('');
    const [phoneNumber, setPhoneNumber] = React.useState<string>('');
    const [error, setError] = React.useState<SetupError | null | undefined>(null);
    const [isGettingVerificationCode, setIsGettingVerificationCode] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        if (error === PHONE_NUMBER_ALREADY_VALIDATED) {
            history.push(
                `/connect-phone-integration/click-to-call/connected?name=${name}&phoneNumber=${phoneNumber}`
            );
        }
    }, [error, history, name, phoneNumber]);

    const hasAccountError =
        error &&
        (error === PHONE_NUMBER_NEW_INSTANCE_AND_REQUIRES_VERIFICATION ||
            error === PHONE_NUMBER_THROTTLED);

    const hasPhoneNumberError = error && !hasAccountError;

    const handleSubmit = () => {
        setError(null);
        setIsGettingVerificationCode(true);
        api.post('/phones', {
            data: {
                type: 'phone',
                e164number: phoneNumber,
                name: name,
            },
        })
            // @ts-expect-error
            .then((response: Response): Promise<any> => response.json())
            .then((verificationCode: string) => {
                history.push(
                    `/connect-phone-integration/click-to-call/validate-code?name=${name}&phoneNumber=${phoneNumber}&verificationCode=${verificationCode}`
                );
            })
            .catch((errorResponse: HttpErrorObject) => {
                errorResponse.httpResponse.json().then((errorCode: string) => {
                    setError(ERROR_CODE_MAP[errorCode] || null);
                    setIsGettingVerificationCode(false);
                });
            });
    };

    return (
        <RightFrame>
            <ModalMain>
                <ModalHeader>Nutshell {getServiceNoun('CLICKTOCALL')} setup</ModalHeader>
                <ModalSubheader>
                    When you make a call with Nutshell, we will ensure that the Caller ID shows your
                    real phone number. Let’s confirm the phone number you want to appear in Caller
                    ID.
                </ModalSubheader>
                {hasAccountError && error && getErrorFlashBannerFromCode(error)}
                <div className='pb-8'>
                    <ModalTitle>Enter your Caller ID information</ModalTitle>
                </div>
                <FormField
                    innerId='name'
                    label='Your name'
                    isRequired={true}
                    // @ts-expect-error
                    hasError={false}
                    errorMessage={null}
                >
                    <TextField
                        id='name'
                        // @ts-expect-error
                        name='name'
                        size='normal'
                        placeholder='Enter your name'
                        type='text'
                        onChange={(newName: string) => setName(newName)}
                        value={name}
                        focusOnMount={true}
                        ignorePasswordAutoFill={true}
                    />
                </FormField>
                <FormField
                    innerId='phoneNumber'
                    label='Your phone number'
                    isRequired={true}
                    // @ts-expect-error
                    hasError={hasPhoneNumberError}
                    errorMessage={
                        error && hasPhoneNumberError ? getErrorMessageFromCode(error) : null
                    }
                >
                    <TextField
                        id='phoneNumber'
                        // @ts-expect-error
                        name='phoneNumber'
                        size='normal'
                        placeholder='###-###-####'
                        type='tel'
                        onChange={(newPhoneNumber: string) => setPhoneNumber(newPhoneNumber)}
                        value={phoneNumber}
                    />
                </FormField>
                <Body>Nutshell will call you to verify your phone number.</Body>
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={0} slideCount={4} />}
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={history.goBack}>
                            Back
                        </Button>
                        <SaveButton
                            variant='primary'
                            size='big'
                            // @ts-expect-error
                            onClick={handleSubmit}
                            buttonText='Call me'
                            savingText='Calling…'
                            isSaving={isGettingVerificationCode}
                            disabled={!name || !phoneNumber || isGettingVerificationCode}
                            errorSaving={Boolean(error)}
                        />
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}
